import { ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LockType } from '@app/services/plan-lock/plan-lock-dialog.model';
import { PlanLockDialogService } from '@app/services/plan-lock/plan-lock-dialog.service';
import { resolveDropdownItemIcons } from '@app/shared/dropdown-item-icons.helper';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { mgcIconStopwatchRegular } from '@sb/mgc-icons/mgcIcon-stopwatch_regular.icon';
import { mgcIconUnlockRegular } from '@sb/mgc-icons/mgcIcon-unlock_regular.icon';
import { hasPermission, type PermissionState } from '@sb/shared/permission/util';
import { IconRegistry, type Icon } from '@sb/ui/icon';
import { SplitButtonMenuItem } from '@sb/ui/split-button';
import { PlanType, SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { clsx } from 'clsx';
import { combineLatest, Subscription } from 'rxjs';

import { getAccountSubscription } from '../../../../../reducers/account/account-service.selectors';
import { DepartmentModel } from '../../../../../reducers/orm/department/department.model';
import { ScheduleFilterPeriod } from '../../../../../reducers/page-filters/page-filters.model';
import { lazySelect } from '../../../../../shared/lazy-select.observable';
import { Modal } from '../../../shared/interfaces';
import { getPermissionState } from './../../../../../reducers/auth/permission.helper';
import { PeriodType } from './../../../../../shared/interfaces';
import { Action } from './../../../shared/interfaces';
import { getTimesheetPageOptionsForSelectedDepartments } from './../../../shared/timesheet-helper.service';

@Component({
  standalone: false,
  selector: 'timesheet-team-dropdown',
  templateUrl: './timesheet-team-dropdown.component.html',
  host: {
    '[class]': 'hostClasses',
  },
})
export class TimesheetTeamDropdownComponent implements OnInit, OnChanges, OnDestroy {
  private translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private planLockDialogService = inject(PlanLockDialogService);

  @Input()
  department: DepartmentModel;

  @Input()
  isClosed: boolean;

  primaryAction?: Action;

  private periodType: PeriodType;
  private formattedDate: string;
  private subscription = new Subscription();
  schedulePeriod: ScheduleFilterPeriod;

  accountSubscription: SubscriptionModel;

  private canCreateTimesheets: boolean;
  private canOpenOrCloseDays: boolean;
  private canClockOthers: boolean;

  isLocked: boolean;
  menuItems: SplitButtonMenuItem[];

  hostClasses = clsx('relative inline-flex align-middle');

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconLockRegular, mgcIconStopwatchRegular, mgcIconUnlockRegular]);
  }

  ngOnInit(): void {
    this.subscription.add(
      combineLatest(
        this.route.data,
        this.store.pipe(lazySelect(getPermissionState)),
        this.store.pipe(lazySelect(getTimesheetPageOptionsForSelectedDepartments)),
        this.store.pipe(lazySelect(getAccountSubscription)),
        this.planLockDialogService.isLocked,
      ).subscribe(([data, permissionState, options, accountSubscription, isLocked]) => {
        this.periodType = data.periodType;

        this.formattedDate = toISODateString(data.period);
        this.isLocked = isLocked;
        this.menuItems = [];
        this.accountSubscription = accountSubscription;

        this.canCreateTimesheets = this.hasCreateTimesheets(permissionState);
        this.canOpenOrCloseDays = options.canOpenOrClose;
        this.canClockOthers = hasPermission(
          {
            permissions: 'Clock time',
            departments: this.department.id,
            userId: 'me',
          },
          permissionState,
        );
        this.createActionButtons();
      }),
    );
  }

  ngOnChanges(): void {
    this.createActionButtons();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private createActionButtons() {
    this.menuItems = [];
    if (this.canCreateTimesheets && !this.isClosed) {
      this.menuItems.push({
        label: this.translate.instant('Add worked hours'),
        command: () => this.openModal('timesheet'),
      });
    }

    if (hasAtLeastSubscriptionPlan(PlanType.PREMIUM, this.accountSubscription) || this.isLocked) {
      if (this.canOpenOrCloseDays || this.isLocked) {
        this.menuItems.push({
          label:
            this.periodType === 'day'
              ? this.translate.instant('Open/Close day')
              : this.translate.instant('Open/Close days'),
          command: () =>
            this.isLocked ? this.openLockModal(LockType.OPEN_CLOSE_DAYS) : this.openModal('open-close-days'),
          ...this.#itemIcons(mgcIconUnlockRegular.name),
        });
      }
    }

    if (this.canClockOthers || this.isLocked) {
      this.menuItems.push({
        label: this.translate.instant('Clock employee'),
        command: () =>
          this.isLocked ? this.openLockModal(LockType.CLOCK_BOUNDARIES) : this.openModal('clock-employee'),
        ...this.#itemIcons(mgcIconStopwatchRegular.name),
      });
    }
  }

  #itemIcons(actionIcon: Icon): Pick<SplitButtonMenuItem, 'icon' | 'trailingIcon'> {
    const { icon, iconEnd } = resolveDropdownItemIcons(actionIcon, { locked: this.isLocked });

    return { icon, trailingIcon: iconEnd };
  }

  openModal(modal: Modal) {
    switch (modal) {
      case 'open-close-days':
        this.router.navigate(['', { outlets: { modal: [modal, this.periodType, this.formattedDate] } }]);
        break;
      case 'clock-employee':
        this.router.navigate(['', { outlets: { modal: [modal, { department: this.department.id }] } }]);
        break;
      default:
        this.router.navigate([
          '',
          { outlets: { modal: [modal, { department: this.department.id, date: this.formattedDate }] } },
        ]);
        break;
    }

    this.cd.detectChanges();
  }

  openLockModal(lockType: LockType) {
    this.planLockDialogService.openLockDialog(lockType);
  }

  hasCreateTimesheets(permissionState: PermissionState) {
    return hasPermission(
      {
        permissions: ['Create timesheets', 'Create own timesheet'],
        userId: 'me',
      },
      permissionState,
    );
  }
}
